import React, { useState } from "react";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Download, Users, CircleCheck, FlaskConical, GraduationCap, ArrowRight, ExternalLink } from "lucide-react";
import TargetBlock from "./TargetBlock";
import ClientLeads from "./ClientLeads";

export const StepValidate = ({ state, patch }) => {
  const bp = state.blueprint;
  const cfg = state.config || {};
  const workshop = cfg.workshop || {};
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [journey, setJourney] = useState(false);
  const symbol = bp?.target_currency === "USD" ? "$" : "₹";
  const combo = bp?.target_combinations?.[bp?.selected_combination ?? 0];

  if (!bp) return <p className="text-sm text-slate-500" data-testid="accelerator-no-blueprint-validate">Create your blueprint first.</p>;

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/accelerator/handoff");
      patch({ handoff: data.handoff, progress: data.progress, blueprint: { ...bp, status: "completed" } });
      setJourney(true);
      toast.success("Your validation handoff is saved — now let's find your first paid client");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not prepare your handoff");
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setExporting(true);
    try {
      const res = await api.get("/accelerator/blueprint/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "2nd_income_blueprint.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed. Please retry.");
    } finally {
      setExporting(false);
    }
  };

  const details = [
    ["YOUR FIRST TEST", bp.first_validation_action],
    ["VALIDATION QUESTION", bp.validation_question],
    ["WHAT YOU STILL NEED TO PROVE", bp.missing_proof],
  ];

  return (
    <section className="space-y-4" data-testid="accelerator-step-validate-panel">
      <div className="card-soft p-5">
        <h2 className="text-base md:text-lg font-bold text-slate-900">Step 6 — Your 2nd Income Validator</h2>
        <p className="text-sm text-slate-600 mt-1.5">Set the target you want to test, then go after a real first client. Your full blueprint stays on the previous step and in the PDF.</p>
      </div>

      <TargetBlock state={state} patch={patch} />

      <div className="accent-bar card-soft p-5" style={{ "--accent": "#0284C7" }} data-testid="validate-target-card">
        <p className="text-[11px] font-mono uppercase tracking-wider text-sky-700">Your {symbol}{Number(bp.validation_amount ?? bp.target_amount).toLocaleString()} validation target</p>
        <p className="num-display mt-2 text-3xl">{combo ? `${combo.customers} × ${symbol}${Number(combo.price_each).toLocaleString()}` : `${symbol}${Number(bp.validation_amount ?? bp.target_amount).toLocaleString()}`}</p>
        <p className="mt-2 text-xs text-slate-600">Target additional income — a target to test against, not a guarantee or prediction.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3" data-testid="validate-details">
        {details.map(([label, value]) => value && (Array.isArray(value) ? value.length : true) ? (
          <div key={label} className="accent-bar card-soft p-4 pl-5" style={{ "--accent": "#0D9488" }} data-testid={`validate-block-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
            {Array.isArray(value)
              ? <ul className="mt-2 space-y-1.5 text-sm text-slate-700">{value.map((v, i) => <li key={i} className="flex gap-2"><span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" /><span>{v}</span></li>)}</ul>
              : <p className="mt-2 text-sm text-slate-700">{value}</p>}
          </div>
        ) : null)}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
        <Button onClick={start} disabled={busy} className="h-12 bg-sky-600 hover:bg-sky-700 text-white" data-testid="validate-start-sprint">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />} Find Me My 1st Paid Client <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <Button onClick={download} disabled={exporting} variant="outline" className="h-12 border-slate-200 text-slate-700" data-testid="validate-export-pdf">
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Download blueprint (PDF)
        </Button>
      </div>

      {state.handoff && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="validate-handoff-ready">
          <p className="inline-flex items-center gap-2 font-semibold text-emerald-700"><CircleCheck className="w-4 h-4" /> Handoff ready</p>
          <p className="mt-1.5 text-sm text-slate-700">{cfg.cta_note || "Your blueprint and handoff are saved."}</p>
          <p className="mt-2 text-xs text-slate-500">Saved {new Date(state.handoff.created_at).toLocaleString()} · blueprint v{bp.version}</p>
        </div>
      )}

      {(journey || state.handoff) && (
        <div className="space-y-4 rise-in" data-testid="journey-sections">
          <ClientLeads state={state} symbol={symbol} />

          <div className="accent-bar card-soft p-5 pl-6" style={{ "--accent": "#0284C7" }} data-testid="journey-validation-section">
            <p className="inline-flex items-center gap-2 text-sm font-bold text-slate-900"><FlaskConical className="w-4 h-4 text-sky-600" /> Validation</p>
            <p className="mt-2 text-xs text-slate-500">Reserved for the guided validation script — the outreach, questions and evidence you gather before selling anything.</p>
          </div>

          {workshop.enabled !== false && (
            <div className="accent-bar card-soft p-5 pl-6" style={{ "--accent": "#059669" }} data-testid="journey-growth-workshop-section">
              <p className="inline-flex items-center gap-2 text-sm font-bold text-slate-900"><GraduationCap className="w-4 h-4 text-emerald-600" /> Growth Workshop</p>
              <p className="mt-2 text-sm font-semibold text-slate-800">{workshop.headline}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{workshop.body}</p>
              {workshop.url
                ? <a href={workshop.url} target="_blank" rel="noreferrer"
                      onClick={() => api.post("/accelerator/events", { event: "growth_workshop_clicked", meta: { url: workshop.url } }).catch(() => {})}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-500" data-testid="growth-workshop-link">
                    {workshop.cta_label || "Book a session"} <ExternalLink className="w-3 h-3" />
                  </a>
                : <p className="mt-3 text-xs text-slate-500" data-testid="growth-workshop-no-link">Booking opens shortly — sessions are being scheduled now.</p>}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default StepValidate;
